import { Table, Tag, Input, Card, Row, Col, Typography } from "antd";
import React, { useEffect, useMemo, useState } from "react";
import MasterBinService from "../../services/Shared-Service/MasterBinService";
import LoadingSpin from "../loadingSpin/LoadingSpin";
import { SearchOutlined } from "@ant-design/icons";
import { useLocation } from "react-router";
import "../../styles/global-ui.css";

const { Text } = Typography;

const BinMaststerTable = () => {
  const location = useLocation();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);

  const locationMap = {
    garment_super_market: "Garment Super Market",
    dispatch_center: "Dispatch Center",
    washing: "Washing",
    "In Transit": "In Transit",
  };

  const getDecodedQueryString = () => {
    const decodedLocation = decodeURIComponent(location.search.substring(1));
    setSelectedLocation(decodedLocation || null);
  };

  const getFormattedLocation = (text, record) => {
    const currentLocation = locationMap[text] || text;

    if (record?.movement_status === "In Transit" && record?.bin_to) {
      const destinationLocation = locationMap[record.bin_to] || record.bin_to;
      return `${currentLocation} → ${destinationLocation}`;
    }

    return currentLocation || "No Data";
  };

  const safeParseSizeQty = (sizeQty) => {
    try {
      if (!sizeQty) return {};
      if (typeof sizeQty === "object") return sizeQty;
      return JSON.parse(sizeQty);
    } catch {
      return {};
    }
  };

  const sortNoDataLast = (list) => {
    return [...list].sort((a, b) => {
      const aNoData = a.total_qty === "No Data";
      const bNoData = b.total_qty === "No Data";

      if (aNoData && !bNoData) return 1;
      if (!aNoData && bNoData) return -1;

      if (!aNoData && !bNoData) {
        return Number(b.total_qty || 0) - Number(a.total_qty || 0);
      }

      return Number(a.bin_id || 0) - Number(b.bin_id || 0);
    });
  };

  useEffect(() => {
    const fetchData = async () => {
      getDecodedQueryString();

      try {
        setLoading(true);

        const response = await MasterBinService.masterBin_get_bins_details();

        const groupedData =
          response?.data?.reduce((acc, item) => {
            const key = `${item.bin_id}-${item.bin_location}`;

            if (!acc[key]) {
              acc[key] = {
                ...item,
                po_numbers: new Set(),
                item_numbers: new Set(),
                kanban_numbers: new Set(),
                colorSizeDetails: [],
                calculatedTotalQty: 0,
              };
            }

            if (item.po_no) acc[key].po_numbers.add(item.po_no);
            if (item.item_no) acc[key].item_numbers.add(item.item_no);

            const kanbanList =
              Array.isArray(item.kanbans) && item.kanbans.length > 0
                ? item.kanbans
                : item.kanban_no
                ? [
                    {
                      kanban_no: item.kanban_no,
                      size_qty: item.size_qty,
                      total_qty: item.total_qty,
                    },
                  ]
                : [];

            kanbanList.forEach((kb) => {
              if (!kb?.kanban_no) return;

              acc[key].kanban_numbers.add(kb.kanban_no);

              const sizeQtyObj = safeParseSizeQty(kb.size_qty);

              Object.entries(sizeQtyObj).forEach(([size, qty]) => {
                const numericQty = Number(qty) || 0;

                acc[key].colorSizeDetails.push({
                  key: `${key}-${kb.kanban_no}-${
                    item.color || "no-color"
                  }-${size}`,
                  kanban_no: kb.kanban_no,
                  color: item.color || "No Data",
                  size,
                  qty: numericQty,
                });

                acc[key].calculatedTotalQty += numericQty;
              });
            });

            return acc;
          }, {}) || {};

        const processedData = Object.values(groupedData).map((item) => ({
          ...item,
          po_no:
            Array.from(item.po_numbers).filter(Boolean).join(", ") || "No Data",
          item_no:
            Array.from(item.item_numbers).filter(Boolean).join(", ") ||
            "No Data",
          kanban_no:
            Array.from(item.kanban_numbers).filter(Boolean).join(", ") ||
            "No Data",
          total_qty:
            item.colorSizeDetails?.length > 0
              ? item.calculatedTotalQty
              : "No Data",
          formattedLocation: getFormattedLocation(item.bin_location, item),
        }));

        const sortedData = sortNoDataLast(processedData);

        setData(sortedData);
        setFilteredData(sortedData);
      } catch (error) {
        console.error("Error fetching bin details:", error);
        setData([]);
        setFilteredData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const searchLower = searchText.toLowerCase();

    const filtered = data.filter((item) => {
      const formattedLocation = getFormattedLocation(item.bin_location, item);

      const locationMatch = selectedLocation
        ? formattedLocation
            .toLowerCase()
            .includes(selectedLocation.toLowerCase())
        : true;

      const binMatch = searchText
        ? String(item.bin_id).toLowerCase().includes(searchLower)
        : true;

      return binMatch && locationMatch;
    });

    setFilteredData(sortNoDataLast(filtered));
  }, [searchText, selectedLocation, data]);

  const totalRecords = filteredData.length;

  const totalQty = useMemo(() => {
    return filteredData.reduce((sum, item) => {
      if (item.total_qty === "No Data") return sum;
      return sum + Number(item.total_qty || 0);
    }, 0);
  }, [filteredData]);

  const columns = [
    {
      title: "Bin ID",
      dataIndex: "bin_id",
      key: "bin_id",
      width: 120,
    },
    {
      title: "PO Number",
      dataIndex: "po_no",
      key: "po_no",
      width: 220,
    },
    {
      title: "Item",
      dataIndex: "item_no",
      key: "item_no",
      width: 180,
    },
    {
      title: "Total Quantity",
      dataIndex: "total_qty",
      key: "total_qty",
      width: 140,
      render: (text) =>
        text === "No Data" ? "No Data" : Number(text || 0).toLocaleString(),
    },
    {
      title: "Location",
      dataIndex: "bin_location",
      key: "bin_location",
      width: 220,
      render: (text, record) => getFormattedLocation(text, record),
    },
    {
      title: "Status",
      dataIndex: "bin_status",
      key: "bin_status",
      width: 140,
      render: (text) => text || "No Data",
    },
  ];

  const expandColumns = [
    {
      title: "Kanban No",
      dataIndex: "kanban_no",
      key: "kanban_no",
    },
    {
      title: "Color",
      dataIndex: "color",
      key: "color",
      render: (color) => <Tag color="blue">{color}</Tag>,
    },
    {
      title: "Size",
      dataIndex: "size",
      key: "size",
    },
    {
      title: "Quantity",
      dataIndex: "qty",
      key: "qty",
      render: (qty) => Number(qty || 0).toLocaleString(),
    },
  ];

  const expandedRowRender = (record) => {
    if (!record.colorSizeDetails?.length) {
      return (
        <div style={{ padding: 16, color: "#64748b", fontWeight: 500 }}>
          No kanban details available for this bin
        </div>
      );
    }

    return (
      <Table
        columns={expandColumns}
        dataSource={record.colorSizeDetails}
        pagination={false}
        rowKey="key"
        size="small"
        className="premium-report-table"
      />
    );
  };

  if (loading) {
    return (
      <div className="dashboard-loading-wrap">
        <LoadingSpin />
      </div>
    );
  }

  return (
    <>
      <Card
        className="modern-card dashboard-filter-wrap"
        style={{ marginBottom: 20 }}
      >
        <Row gutter={[16, 16]} align="middle" justify="space-between">
          <Col xs={24} lg={8}>
            <div className="page-title-wrap">
              <Text style={{ fontSize: 16, fontWeight: 600, color: "#1e293b" }}>
                Master Bin Search
              </Text>
              <Text className="page-subtitle">Search by Bin ID</Text>
            </div>
          </Col>

          <Col xs={24} lg={10}>
            <Input
              placeholder="Search Bin ID..."
              prefix={<SearchOutlined style={{ color: "#64748b" }} />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
              size="large"
              style={{
                borderRadius: 12,
                height: 42,
                boxShadow: "0 2px 10px rgba(15, 23, 42, 0.06)",
              }}
            />
          </Col>
        </Row>
      </Card>

      <div className="compact-stats" style={{ marginBottom: 20 }}>
        <Row gutter={[16, 8]} justify="center">
          <Col xs={12} sm={8}>
            <div className="compact-stat-item">
              <div className="compact-stat-value" style={{ color: "#0f172a" }}>
                {totalRecords}
              </div>
              <div className="compact-stat-label">Filtered Records</div>
            </div>
          </Col>

          <Col xs={12} sm={8}>
            <div className="compact-stat-item">
              <div className="compact-stat-value" style={{ color: "#0f766e" }}>
                {totalQty.toLocaleString()}
              </div>
              <div className="compact-stat-label">Total Quantity</div>
            </div>
          </Col>
        </Row>
      </div>

      <Card className="modern-card">
        <Table
          title={() => (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 16,
                flexWrap: "wrap",
                padding: "8px 4px 4px",
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: 18,
                    fontWeight: 700,
                    color: "#0f172a",
                    marginBottom: 4,
                  }}
                >
                  Master Bin Report
                </div>
                <div
                  style={{
                    fontSize: 13,
                    color: "#64748b",
                    fontWeight: 500,
                  }}
                >
                  View grouped bin records with expandable kanban, color, and
                  size details
                </div>
              </div>

              <div
                style={{
                  padding: "8px 14px",
                  borderRadius: 12,
                  background:
                    "linear-gradient(135deg, #f8fafc 0%, #eef2ff 100%)",
                  border: "1px solid #e2e8f0",
                  fontSize: 13,
                  fontWeight: 600,
                  color: "#334155",
                }}
              >
                Records: {filteredData.length}
              </div>
            </div>
          )}
          columns={columns}
          dataSource={filteredData}
          expandable={{
            expandedRowRender,
          }}
          rowKey={(record) => `${record.bin_id}-${record.bin_location}`}
          size="middle"
          className="premium-report-table"
          scroll={{ x: 1200 }}
          pagination={{
            pageSize: 10,
            showSizeChanger: false,
            showQuickJumper: false,
            position: ["bottomRight"],
            itemRender: (page, type, originalElement) => {
              if (type === "prev") {
                return <span className="premium-page-arrow">‹</span>;
              }

              if (type === "next") {
                return <span className="premium-page-arrow">›</span>;
              }

              return originalElement;
            },
          }}
        />
      </Card>
    </>
  );
};

export default BinMaststerTable;